import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { Avatar, Box, Chip, Tooltip, Typography, useTheme } from '@mui/material'
import { GridRenderCellParams, GridRowId } from '@mui/x-data-grid-premium'
import { stringAvatar } from 'components/DataGrid/gridOperators'
import { DensityStyleTypes } from 'types'

interface RenderNameCellProps {
  params: GridRenderCellParams
  densityStyles: DensityStyleTypes
  handleRowClick: (row: GridRenderCellParams['row']) => void
  toggleExpanded: (id: GridRowId) => void
}
const RenderNcoNameCell: React.FC<RenderNameCellProps> = ({
  params,
  handleRowClick,
  densityStyles,
  toggleExpanded,
}) => {
  const theme = useTheme()
  const { rowNode, row: _row, value } = params
  const row = params.row
  const { PK, SK, organization, isClone, isParent } = row
  const pinnedRow = rowNode?.type === 'pinnedRow'
  const avatarSize = pinnedRow ? densityStyles.avatarSizePinned : densityStyles.avatarSize
  const _ncoStatus = params?.row?.ncoStatus
  const isChild = !isParent
  const depth = rowNode?.depth ?? 0
  const indent = depth * 20

  // only parent rows get an expand button
  const isGroup = isParent
  // cast away TS so we can see the internal children array
  const internalNode = rowNode as any
  // const hasChildren = (internalNode.children?.length ?? 0) > 0
  const hasChildren = params?.row?.hasChildRows
  const isExpanded = Boolean(internalNode.childrenExpanded)
  const _route = isParent ? `/deals/${PK}/${SK}/details` : `/deals/${row?.PK}/DEAL_${row?.deal_uuid}/agreement`
  //@ts-ignore
  if (rowNode?.isAutoGenerated) return
  // Server aggregation row - render simple text label
  if (params?.row?.isAggregationRow) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          padding: densityStyles.padding,
          fontWeight: 600,
          color: 'text.secondary',
        }}
      >
        {params?.row?.name || 'Total (All Filtered)'}
      </Box>
    )
  }
  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        pl: isChild ? `${indent}px` : densityStyles.padding,
        pt: densityStyles.padding,
        pb: densityStyles.padding,
        pr: densityStyles.padding,
        gap: densityStyles.gap,
        width: '100%',
        backgroundColor: 'transparent',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
      }}
      onClick={() => handleRowClick(row)}
    >
      <>
        {/* {isGroup && !pinnedRow && (
          <IconButton
            className="nco-expand-button"
            size="small"
            disabled={!hasChildren} // gray out if no children
            onClick={e => {
              e.stopPropagation()
              toggleExpanded(params.id)
            }}
            sx={{ color: hasChildren ? 'inherit' : 'text.disabled' }}
          >
            {isExpanded ? (
              <ExpandMoreIcon className="nco-expand-button" />
            ) : (
              <ChevronRightIcon
                className="nco-expand-button"
                sx={{ color: hasChildren ? 'text.primary' : 'text.disabled' }}
              />
            )}
          </IconButton>
        )} */}

        {/* child-dot only on non-parent rows */}
        {isChild && (
          <FiberManualRecordIcon
            sx={{
              fontSize: 8,
              color: 'text.disabled',
              ml: 1,
              mr: 1,
            }}
          />
        )}
        {isParent ? (
          <>
            {isParent &&
              (organization?.photoUrl ? (
                <Avatar
                  src={organization.photoUrl}
                  sx={{
                    width: avatarSize,
                    height: avatarSize,
                    objectFit: 'cover',
                    objectPosition: 'center',
                    ml: 1,
                  }}
                  alt={value}
                />
              ) : (
                <Avatar
                  {...stringAvatar(value, densityStyles)}
                  sx={{
                    backgroundColor: 'rgb(114, 229, 218)',
                    width: avatarSize,
                    height: avatarSize,
                    ml: 1,
                  }}
                  alt={value}
                />
              ))}
            <Tooltip
              title={<Typography sx={{ backgroundColor: 'transparent' }}>{params?.value}</Typography>}
              enterDelay={2000}
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '.MuiTooltip-tooltip': {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  padding: 0,
                },
              }}
            >
              <Box
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flexGrow: 1, // Takes up remaining space
                  color: isClone ? 'text.subtask' : '',
                  fontWeight: isClone ? 'bold' : '',
                }}
              >
                <Typography sx={{ fontSize: densityStyles.fontSize, textAlign: 'left' }}>{value || ''}</Typography>
              </Box>
            </Tooltip>

            {/* {ncoStatus && <AddRoadIcon sx={{ color: 'rgba(159, 90, 253, 1)', height: 20, width: 20 }} />} */}
            {/* {row?.isHosting && (
              <Typography sx={{ color: 'icon.website', fontSize: densityStyles.fontSize - 1 }}>Hosting</Typography>
            )}
            {row?.isSEO && (
              <Typography sx={{ color: 'icon.seo', fontSize: densityStyles.fontSize - 1 }}>SEO</Typography>
            )}
            {row?.isDigital && (
              <Typography sx={{ color: 'icon.nco', fontSize: densityStyles.fontSize - 1 }}>DIGITAL</Typography>
            )} */}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.8 }}>
              {row?.isHosting && (
                <Chip
                  size="small"
                  style={{
                    backgroundColor: theme.palette.icon.website,
                    width: densityStyles.labelChipSize,
                    height: densityStyles.labelChipSize,
                    borderRadius: '50%',
                  }}
                />
              )}
              {row?.isSEO && (
                <Chip
                  size="small"
                  style={{
                    backgroundColor: theme.palette.icon.seo,
                    width: densityStyles.labelChipSize,
                    height: densityStyles.labelChipSize,
                    borderRadius: '50%',
                  }}
                />
              )}
              {row?.isDigital && (
                <Chip
                  size="small"
                  style={{
                    backgroundColor: theme.palette.icon.nco,
                    width: densityStyles.labelChipSize,
                    height: densityStyles.labelChipSize,
                    borderRadius: '50%',
                  }}
                />
              )}
            </Box>
          </>
        ) : (
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              fontSize: densityStyles.fontSize,
              fontWeight: isParent ? 600 : 400,
              ml: isParent ? 1 : 0,
              color: row?.isProduct ? 'text.highlight' : 'text.primary',
            }}
          >
            {value}
          </Typography>
        )}
      </>
    </Box>
  )
}
export default RenderNcoNameCell
